import { ICache, RemoveEventHandle } from "csi/typings";
import Writer from "csi/xml/Writer";
import { submitWriter } from "./writerService";

export type WriterErrorFunction = (writer: Writer) => void;
export type WriterOperationFunction = (writer: Writer) => void;

/** caches for which changes have been held */
let cacheSet = new Set<ICache>();

/** function to call when error occurs publishing operations */
let errorCallback: WriterErrorFunction;

/** timer for publishing held changes */
let holdTimer: any;

/** writer for changes put on hold */
const holdWriter = new Writer();
holdWriter.bShared = true;

/** set of callbacks to be called before saving held changes */
const preSaveCallbacks = new Set<VoidFunction>();

/**
 * Clear timer for publishing held changes
 */
function clearHoldTimer(): void {
	if (holdTimer) {
		clearTimeout(holdTimer);
		holdTimer = null;
	}
}

/**
 * Publish changes in writer now
 */
async function publishNow(asyncRequest = false): Promise<void> {
	const caches = cacheSet;
	cacheSet = new Set();

	clearHoldTimer();

	for (const callback of preSaveCallbacks) {
		callback();
	}

	// if unloading the site, then do not wait for a response
	if (asyncRequest) {
		holdWriter.addParameter("AsyncRequest", "True");
	}

	try {
		const response = await submitWriter(holdWriter);
		caches.forEach((cache) => cache.cacheResponse(response));
	} catch (err) {
		// if writer is given, then responsibility of caller to handle errors; if not, then use default error handling
		errorCallback?.(err);
	}
}

/**
 * Publish changes held over from previous action(s)
 */
export async function submitHoldWriter(hold?: boolean, asyncRequest?: boolean): Promise<void> {
	if (holdWriter.isChanged()) {
		if (holdWriter.cache) {
			cacheSet.add(holdWriter.cache);
		}

		if (hold) {
			// restart timer - delay here to allow user time to choose next attribute to edit; if none, then publish
			clearHoldTimer();
			holdTimer = setTimeout(() => {
				publishNow(asyncRequest);
			}, 3000);
		} else {
			try {
				// if not holding changes, then continue with publishing
				await publishNow(asyncRequest);
			} catch (err) {
				// ignore - the errorCallback will take care of any errors
			}
		}
	}
}

/**
 * Set error callback to be used when errors submitting writer operations to server
 */
export function registerErrorCallback(callback: WriterErrorFunction): void {
	errorCallback = callback;
}

/**
 * Add callback to be called before held changes are saved
 */
export function registerHoldPreSaveCallback(callback: VoidFunction): RemoveEventHandle {
	preSaveCallbacks.add(callback);

	return {
		remove(): void {
			preSaveCallbacks.delete(callback);
		},
	};
}

/**
 * Request writer to be used for quick node operations; indicate if want to publish results
 * immediately after applying operations - in most cases bPublish should be true
 */
export async function requestWriter(cache: ICache, callback: WriterOperationFunction, publish?: boolean, hold?: boolean): Promise<void> {
	// clear timer so that new changes can be applied
	clearHoldTimer();

	// apply cache to the writer
	holdWriter.cache = cache;

	// apply function to writer
	callback?.(holdWriter);

	// start timer to publish changes
	if (publish) {
		await submitHoldWriter(hold);
	}
}

